import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { invInput, invField, money, qty, StockBadge, errMsg } from './invApi';
import { ItemHistory } from './ItemHistory';

const Lbl = ({ children }) => <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{children}</p>;

const AdjustBox = ({ api, item, onDone }) => {
  const [count, setCount] = useState('');
  const [reason, setReason] = useState('');
  const save = async () => {
    try { await api.post(`/items/${item.id}/adjust`, { counted_qty: Number(count), reason }); toast.success('Stock ajusté'); setCount(''); setReason(''); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-end">
      <div className="w-28"><Lbl>Compté</Lbl><input type="number" className={invInput} value={count} onChange={e => setCount(e.target.value)} data-testid="item-adjust-count" /></div>
      <div className="flex-1 min-w-[140px]"><Lbl>Raison</Lbl><input className={invInput} value={reason} onChange={e => setReason(e.target.value)} placeholder="Décompte, bris, perte…" data-testid="item-adjust-reason" /></div>
      <button onClick={save} disabled={count === '' || reason.trim().length < 3} className="px-3 py-2 rounded-lg bg-slate-700 text-white text-sm disabled:opacity-40" data-testid="item-adjust-save">Ajuster</button>
    </div>
  );
};

export const ItemDialog = ({ api, itemId, canSeeCosts, onClose }) => {
  const [item, setItem] = useState(null);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({});

  const toForm = (it) => ({ name: it.name, part_number: it.part_number, category_id: it.category_id, strategy: it.strategy, min_qty: it.min_qty, location: { row: '', shelf: '', bin: '', ...it.location } });
  const refresh = () => api.get(`/items/${itemId}`).then(setItem).catch(() => {});
  useEffect(() => {
    let alive = true;
    api.get(`/items/${itemId}`).then(it => { if (alive) { setItem(it); setF(toForm(it)); } }).catch(() => toast.error('Pièce introuvable'));
    if (canSeeCosts) api.get('/categories').then(c => alive && setCats(c));
    return () => { alive = false; };
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try { await api.patch(`/items/${itemId}`, { ...f, min_qty: Number(f.min_qty) || 0 }); toast.success('Enregistré'); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const loc = (k, v) => setF({ ...f, location: { ...f.location, [k]: v.toUpperCase() } });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-3 pr-6">{item?.name || '…'} {item && <StockBadge item={item} />}</DialogTitle></DialogHeader>
        {item && (
          <div className="space-y-5">
            {canSeeCosts && (
              <div className="grid grid-cols-2 gap-3">
                <div><Lbl>Nom</Lbl><input className={invInput} value={f.name || ''} onChange={e => setF({ ...f, name: e.target.value })} data-testid="item-name" /></div>
                <div><Lbl>No pièce</Lbl><input className={invInput} value={f.part_number || ''} onChange={e => setF({ ...f, part_number: e.target.value })} data-testid="item-pn" /></div>
              </div>
            )}
            <div>
              <Lbl>Emplacement (rangée · tablette · bac)</Lbl>
              <div className="flex gap-2">
                {['row', 'shelf', 'bin'].map(k => <input key={k} className={`${invField} w-20 font-mono text-center`} value={f.location?.[k] || ''} onChange={e => loc(k, e.target.value)} placeholder={{ row: 'A', shelf: '3', bin: '5' }[k]} data-testid={`item-loc-${k}`} />)}
              </div>
            </div>
            {canSeeCosts && (
              <div className="grid grid-cols-3 gap-3">
                <div><Lbl>Approvisionnement</Lbl>
                  <select className={invInput} value={f.strategy} onChange={e => setF({ ...f, strategy: e.target.value })} data-testid="item-strategy">
                    <option value="stocked">Gardée en stock</option><option value="on_demand">Commandée à la demande</option>
                  </select></div>
                <div><Lbl>Minimum</Lbl><input type="number" className={invInput} value={f.min_qty ?? 0} disabled={f.strategy !== 'stocked'} onChange={e => setF({ ...f, min_qty: e.target.value })} data-testid="item-min" /></div>
                <div><Lbl>Catégorie</Lbl>
                  <select className={invInput} value={f.category_id} onChange={e => setF({ ...f, category_id: e.target.value })} data-testid="item-category">
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
              </div>
            )}
            <button onClick={save} className="px-4 py-2 rounded-full bg-amber-500 text-black text-sm font-semibold" data-testid="item-save">Enregistrer</button>
            {canSeeCosts && (
              <div className="grid grid-cols-3 gap-3 text-sm bg-slate-900 rounded-xl p-3" data-testid="item-costs">
                <div><Lbl>Coût moyen</Lbl>{money(item.avg_cost)}</div>
                <div><Lbl>Dernier coût</Lbl>{money(item.last_cost)}</div>
                <div><Lbl>Prix de vente</Lbl><span className="text-amber-300">{money(item.sale_price)}</span></div>
              </div>
            )}
            {canSeeCosts && <AdjustBox api={api} item={item} onDone={refresh} />}
            <p className="text-xs text-slate-500">Utilisation moyenne : {qty(item.monthly_use)} / mois{item.compatible?.length ? ` · Posée sur : ${item.compatible.join(', ')}` : ''}</p>
            <ItemHistory item={item} canSeeCosts={canSeeCosts} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
